import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bird, ArrowLeft, Mail, MapPin, Send, Phone } from "lucide-react";
import { toast } from "sonner";

const CONTACT_EMAIL = "drkev@drkevinkwok.org";
const CONTACT_PHONE = "+65 8366 9817";
const CONTACT_PHONE_TEL = "+6583669817";
const CONTACT_ADDRESS = "16 Raffles Quay #14-05, Singapore 048581";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSending(true);
    // Open the user's mail client with a prefilled draft. No data is sent server-side.
    const subject = encodeURIComponent(
      `Never Alone SG — message from ${form.name.trim()}`
    );
    const body = encodeURIComponent(
      `${form.message.trim()}\n\n— ${form.name.trim()} (${form.email.trim()})`
    );
    const href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    window.location.href = href;
    setTimeout(() => {
      toast.success("Opening your email app… thank you for writing in.");
      setSending(false);
    }, 400);
  };

  return (
    <div className="min-h-screen bg-[#f6f9fc]">
      <header className="bg-[#354556] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 border border-white/20">
              <Bird className="h-5 w-5 text-[#cfe7f8]" />
            </span>
            <span className="ht-display text-lg">Never Alone SG</span>
          </Link>
          <Link
            to="/"
            className="ht-btn inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 px-4 py-2 text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <p className="text-[#4a90c2] font-semibold uppercase tracking-[0.18em] text-xs ht-body">
          Contact
        </p>
        <h1 className="mt-3 ht-display text-3xl sm:text-4xl text-[#2c3e50] leading-[1.15]">
          Say hello. A real person will reply.
        </h1>
        <p className="mt-5 ht-body text-[#5a6b7d] text-base sm:text-lg leading-relaxed max-w-2xl">
          Questions about the community, privacy, your account, or just want
          to say hi — we&apos;d love to hear from you. We aim to reply within a
          few working days.
        </p>

        <div className="mt-10 grid lg:grid-cols-5 gap-6 lg:gap-10">
          {/* Contact details */}
          <div className="lg:col-span-2 space-y-5">
            <div className="ht-card rounded-2xl bg-white border border-[#e6eef5] p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#eaf4fb] text-[#3d6486]">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="ht-body font-semibold text-[#2c3e50]">Email</p>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="mt-1 inline-block text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline ht-body break-all"
                  >
                    {CONTACT_EMAIL}
                  </a>
                  <p className="mt-2 text-xs text-[#7e8a96]">
                    For privacy and access requests, please write here too.
                  </p>
                </div>
              </div>
            </div>

            <div className="ht-card rounded-2xl bg-white border border-[#e6eef5] p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#eaf4fb] text-[#3d6486]">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <p className="ht-body font-semibold text-[#2c3e50]">Phone</p>
                  <a
                    href={`tel:${CONTACT_PHONE_TEL}`}
                    className="mt-1 inline-block text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline ht-body"
                  >
                    {CONTACT_PHONE}
                  </a>
                  <p className="mt-2 text-xs text-[#7e8a96]">
                    Mon–Fri, 10am–6pm SGT. Calls and WhatsApp.
                  </p>
                </div>
              </div>
            </div>

            <div className="ht-card rounded-2xl bg-white border border-[#e6eef5] p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#eaf4fb] text-[#3d6486]">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <p className="ht-body font-semibold text-[#2c3e50]">
                    Mailing address
                  </p>
                  <p className="mt-1 ht-body text-[#5a6b7d]">
                    {CONTACT_ADDRESS}
                  </p>
                  <p className="mt-2 text-xs text-[#7e8a96]">
                    Postal mail is read, but email gets a faster reply.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-[#eaf4fb] border border-[#cfe7f8] p-6">
              <p className="ht-body text-sm text-[#2c3e50]">
                <span className="font-semibold">In crisis?</span> Never Alone
                in Singapore is a peer community, not a crisis service. Please
                reach Samaritans of Singapore at{" "}
                <a
                  href="tel:1767"
                  className="font-semibold text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
                >
                  1767
                </a>{" "}
                or call{" "}
                <a
                  href="tel:995"
                  className="font-semibold text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
                >
                  995
                </a>{" "}
                for emergencies.
              </p>
            </div>
          </div>

          {/* Message form */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 ht-card rounded-2xl bg-white border border-[#e6eef5] p-6 sm:p-8 shadow-sm"
          >
            <h2 className="ht-display text-xl text-[#2c3e50]">Write to us</h2>
            <p className="mt-2 ht-body text-sm text-[#5a6b7d]">
              This opens a draft in your email app addressed to{" "}
              <span className="font-semibold text-[#3d6486]">
                {CONTACT_EMAIL}
              </span>
              . Nothing is sent until you press send.
            </p>

            <div className="mt-5 space-y-3">
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                className="ht-input w-full rounded-md border border-[#d6dde4] bg-white px-4 py-3 text-[15px] placeholder:text-[#9aa6b2]"
              />
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Your email"
                className="ht-input w-full rounded-md border border-[#d6dde4] bg-white px-4 py-3 text-[15px] placeholder:text-[#9aa6b2]"
              />
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="What's on your mind?"
                rows={6}
                className="ht-input w-full rounded-md border border-[#d6dde4] bg-white px-4 py-3 text-[15px] placeholder:text-[#9aa6b2] resize-y"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="ht-btn mt-5 inline-flex items-center justify-center gap-2 rounded-md bg-[#3d6486] hover:bg-[#345674] text-white font-semibold px-5 py-3 text-[15px] disabled:opacity-70"
            >
              <Send className="h-4 w-4" />
              {sending ? "Opening email…" : "Open in email app"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Contact;
